import { ReactElement, useEffect, useRef, useState } from "react";

import { Status, Wrapper } from "@googlemaps/react-wrapper";
import { useTheme } from "styled-components";

import Map, { dottedLines } from "components/map";
import ToggleMapMode, { MAP_MODE } from "components/map/controls/darkmode-btn/dark-mode-btn";
import ResetMapButton from "components/map/controls/reset-map-button";
import Marker from "components/map/marker";
import Polyline from "components/map/polyline";
import TaskInformation from "components/tracker/info/info";
import { useTrackerContext } from "context/tracker";
import driverIcon from "icons/driver";
import { DEFAULT_ZOOM, LatLngLiteral, MAP_MODE_KEY, Task, TrackerResponse, TrackerState } from "types/types";
import { getBoundsFromLatLng, getTaskMarkerWithCategory } from "utils/utils";

interface Location {
  type: string;
  isActive: boolean;
  task?: Task;
  bounds: LatLngLiteral;
}

const Tracker = () => {
  const { data: tracker, mapsAPIKey, mapMode, setMapMode } = useTrackerContext();
  const theme: any = useTheme();

  appendAssignees(tracker);

  const resetMap = useRef<any>(null);
  const [showResetAction, setShowResetAction] = useState<boolean>(false);
  const activeTask = tracker?.tasks.find((task) => tracker?.active_states?.includes(task.state));
  let locations: Location[] = [];

  useEffect(() => {
    if (localStorage) {
      setMapMode(localStorage.getItem(MAP_MODE_KEY) || MAP_MODE.DARK);
    } else {
      setMapMode(MAP_MODE.DARK);
    }
  }, []);

  // task location
  if (activeTask?.address?.location?.coordinates) {
    tracker?.tasks.map((task, i) => {
      locations[i] = {
        type: "destination",
        isActive: task.id === activeTask.id,
        task: task,
        bounds: {
          lat: task.address?.location?.coordinates[1],
          lng: task.address?.location?.coordinates[0],
        },
      };
    });
  }

  // assignee location
  if (activeTask?.assignee_data?.last_time_location?.location?.coordinates) {
    locations.push({
      type: "origin",
      isActive: false,
      bounds: {
        lat: activeTask?.assignee_data?.last_time_location?.location?.coordinates[1],
        lng: activeTask?.assignee_data?.last_time_location?.location?.coordinates[0],
      },
    });
  }

  const activeDestination = locations.find((location: { isActive: boolean }) => location.isActive)?.bounds;
  const origin = locations.find((location: { type: string }) => location.type === "origin")?.bounds;
  const getMapBounds = () => {
    if (locations.length <= 0) {
      const defaultMapLocation = tracker?.default_map_location?.coordinates;
      if (defaultMapLocation) {
        return getBoundsFromLatLng(defaultMapLocation[0], defaultMapLocation[1]);
      }
    }

    if (locations.length === 1) {
      return getBoundsFromLatLng(locations[0].bounds.lat, locations[0].bounds.lng);
    }

    return locations.map((location: { bounds: { lat: number; lng: number } }) => {
      return location.bounds;
    });
  };
  const showDestination = !!tracker?.show_destination;
  const showPath = !!tracker?.show_path;
  const bounds = getMapBounds();

  return (
    <Wrapper apiKey={mapsAPIKey} render={render}>
      <Map
        center={bounds?.[0]}
        zoom={DEFAULT_ZOOM}
        maxZoom={tracker?.max_zoom_level}
        autoZoom={tracker?.autozoom}
        bounds={bounds}
        resetMap={resetMap}
        isDarkMode={mapMode === MAP_MODE.DARK}
        onCenterChange={() => {
          setShowResetAction(true);
        }}
      >
        {/* delivery location pin */}
        {showDestination &&
          locations &&
          locations
            .filter((loc: { type: string }) => loc.type == "destination")
            .map((location: Location, index: number) => {
              return (
                <Marker
                  position={location.bounds}
                  icon={{
                    url: getTaskMarkerWithCategory(
                      location.task?.state,
                      location.task?.category,
                      mapMode === MAP_MODE.DARK,
                      index + 1,
                      false,
                      theme.icons.taskCategory,
                      theme?.colors?.taskIconBackground
                    ),
                  }}
                  dimension={[70, 70]}
                  infoWindow={location?.task?.address?.formatted_address}
                  key={index}
                />
              );
            })}

        {/* carrier location pin */}
        {tracker?.assignees.map((assignee, index) => {
          if (assignee.last_time_location) {
            const lat = assignee?.last_time_location?.location?.coordinates?.[1];
            const lng = assignee?.last_time_location?.location?.coordinates?.[0];
            if (!assignee?.last_time_location?.location?.coordinates) {
              return;
            }
            const location = {
              lat: lat,
              lng: lng,
            };
            const heading = assignee?.last_time_location?.heading || 0;

            return (
              <Marker
                position={location}
                icon={{
                  url: driverIcon(heading),
                }}
                infoWindow={assignee.display_name}
                dimension={[20, 20]}
                anchor={[10, 10]}
                key={index}
              />
            );
          }
        })}

        {/* polyline between co-ordinates */}
        {origin && activeDestination && showPath && (
          <Polyline
            path={[origin, activeDestination]}
            geodesic={true}
            strokeColor={"#26CC38"}
            strokeOpacity={0}
            icons={dottedLines}
          />
        )}
        {activeTask && (
          <TaskInformation activeTask={activeTask} origin={origin} activeDestination={activeDestination} />
        )}

        <ToggleMapMode
          value={mapMode}
          toggleMapState={() => {
            const state = getState(MAP_MODE_KEY);
            setMapMode(state);
            localStorage.setItem(MAP_MODE_KEY, state);
          }}
        />

        {showResetAction && (
          <ResetMapButton
            onResetAction={() => {
              if (resetMap !== null) {
                resetMap.current(null);
              }
            }}
          />
        )}
      </Map>
    </Wrapper>
  );
};

const render = (status: Status): ReactElement => {
  if (status === Status.LOADING) return <h3>{status} ...</h3>;
  if (status === Status.FAILURE) return <h3>{status} ...</h3>;
  return <></>;
};

const getState = (key: string): MAP_MODE => {
  let mode = localStorage.getItem(key);
  if (mode === MAP_MODE.LIGHT) {
    return MAP_MODE.DARK;
  }
  if (mode === MAP_MODE.DARK) {
    return MAP_MODE.LIGHT;
  }

  return MAP_MODE.LIGHT;
};

const appendAssignees = (payload: TrackerResponse | undefined) => {
  if (payload?.state !== TrackerState.ACTIVE) {
    return;
  }
  payload?.tasks.forEach((task) => {
    task.assignee_data = payload?.assignees.find((v) => v.id === task.assignee);
  });
};

export default Tracker;
